import React, { ChangeEvent, useEffect, useState } from 'react';
import Modal from '@app/react-common/components/modal/Modal';
import Button from '@app/react-common/components/button/Button';
import RadioButtonGroup, { RadioButtonOption } from '@app/react-common/components/radio-button-group/RadioButtonGroup';
import TextArea from '@app/react-common/components/text-area/TextArea';
import { AbTest } from '../../models/AbTest';
import { RuleChips } from '@app/react-common/components/rule-chip/RuleChip';
import { AbTestWinnerOptions } from '../../models/AbtestWinnerOptions';
import { Checkout } from '../../../../checkout/react/models/Checkout';
import { SegmentMetrics } from '../list-ab-tests/AbTestCard/components/AbTestSegmentsMetricsTable/AbTestSegmentsMetricsTable.types';
import { ABTestOutlierFilterOption } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutVariantABTest';
import AbTestSegmentsMetricsTable from '../list-ab-tests/AbTestCard/components/AbTestSegmentsMetricsTable/AbTestSegmentsMetricsTable';

class EndAbTestModalProps {
  title: string;
  buttonText: string;
  abTest: AbTest;
  checkouts: Checkout[];
  comments?: string | null;
  selectedActionOption?: string | null;
  selectedSegments?: SegmentMetrics[] | null; // Restore selected segments when reopening
  segments?: SegmentMetrics[] | null;
  segmentsNoOutliers?: SegmentMetrics[] | null;
  outlierFilter?: ABTestOutlierFilterOption | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: {
    comments: string;
    selectedActionOption: string | null;
    selectedSegments: SegmentMetrics[];
    outlierFilter: ABTestOutlierFilterOption;
  }) => void;
  onActionOptionChanged: (data: {
    comments: string;
    selectedActionOption: string | null;
    selectedSegments: SegmentMetrics[];
  }) => void;
  buttonVariant?: 'primary' | 'secondary' | 'tertiary' | 'danger' | undefined;
}

export const EndAbTestModal: React.FC<EndAbTestModalProps> = (props: EndAbTestModalProps) => {
  const [testComments, setTestComments] = useState('');
  const [selectedActionOption, setSelectedActionOption] = useState<string | null>(null);
  const [selectedSegments, setSelectedSegments] = useState<SegmentMetrics[]>([]);
  const [outlierFilter, setOutlierFilter] = useState<ABTestOutlierFilterOption>(ABTestOutlierFilterOption.WITH_OUTLIER);

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setTestComments(e.target.value);
  };

  const handleActionOptionChange = (option: RadioButtonOption) => {
    setSelectedActionOption(option.value);
  };

  const isReviewDisabled =
    selectedActionOption === AbTestWinnerOptions.implementOnlySpecificSegments ? selectedSegments.length === 0 : false;

  const handleReviewClick = () => {
    // Send comments, selected action option, and selected segments to parent
    props.onSuccess({
      comments: testComments.trim(),
      selectedActionOption: selectedActionOption,
      selectedSegments:
        selectedActionOption === AbTestWinnerOptions.implementOnlySpecificSegments ? selectedSegments : [],
      outlierFilter,
    });
  };

  // 🔥 Prepopulate form fields when modal opens or props change
  useEffect(() => {
    if (props.isOpen) {
      setTestComments(props.comments ?? '');
      setSelectedActionOption(props.selectedActionOption ?? null);
      // Initialize selected segments from props if available, otherwise start empty
      // This allows the modal to remember previous selections when reopened
      setSelectedSegments(props.selectedSegments ?? []);
      setOutlierFilter(props.outlierFilter ?? ABTestOutlierFilterOption.WITH_OUTLIER);
      console.log(
        'Modal opened with comments:',
        props.comments,
        'selected action:',
        props.selectedActionOption,
        'and selected segments:',
        props.selectedSegments?.length ?? 0,
      );
    }
  }, [props.isOpen, props.comments, props.selectedActionOption, props.selectedSegments, props.outlierFilter]);

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose}>
      <div className="end-abtest-modal">
        <h2 className="end-abtest-modal__title">{props.title}</h2>
        <div className="end-abtest-modal__seperator"></div>
        <div className="end-abtest-modal__content">
          <h4 className="end-abtest-modal__label">Test Rules</h4>
          <RuleChips abTestRules={props.abTest.abTestRules} />
          <div className="end-abtest-modal__spacer"></div>
          <h4 className="end-abtest-modal__label">Action</h4>
          <div>
            <RadioButtonGroup
              key={props.isOpen ? (selectedActionOption ?? 'default') : 'closed'}
              options={[
                {
                  label: 'Implement Test Winner in Checkout',
                  value: AbTestWinnerOptions.implementTestWinnerInCheckout,
                },
                { label: 'Implement Only Specific Segments', value: AbTestWinnerOptions.implementOnlySpecificSegments },
                { label: "Don't Implement Any Change", value: AbTestWinnerOptions.dontImplementAnyChange },
              ]}
              disabled={false}
              defaultOptionValue={selectedActionOption}
              onChange={handleActionOptionChange}
            />
          </div>

          {selectedActionOption === AbTestWinnerOptions.implementOnlySpecificSegments && (
            <>
              <div className="end-abtest-modal__spacer"></div>
              <h4 className="end-abtest-modal__label">Outliers Filtering</h4>
              <div>
                <RadioButtonGroup
                  key={`outlier-${props.isOpen}`}
                  options={[
                    { label: 'With Outliers', value: ABTestOutlierFilterOption.WITH_OUTLIER },
                    { label: 'Without Outliers', value: ABTestOutlierFilterOption.WITHOUT_OUTLIER },
                  ]}
                  disabled={false}
                  defaultOptionValue={outlierFilter}
                  onChange={(option) => setOutlierFilter(option.value as ABTestOutlierFilterOption)}
                />
              </div>
              <AbTestSegmentsMetricsTable
                title="Winning Segments"
                segments={
                  outlierFilter === ABTestOutlierFilterOption.WITHOUT_OUTLIER
                    ? props.segmentsNoOutliers || []
                    : props.segments || []
                }
                isLoading={false}
                showCheckbox={true}
                selectedSegmentIds={selectedSegments.map((s) => s.segmentId)}
                onSelectionChange={(newSelected) => setSelectedSegments(newSelected)}
              />
            </>
          )}
          <div
            className={
              selectedActionOption === AbTestWinnerOptions.implementOnlySpecificSegments
                ? 'end-abtest-modal__spacer'
                : 'end-abtest-modal__less-spacer'
            }></div>
          <TextArea
            placeholder="Test comments..."
            label="Test Comments"
            name="testComments"
            required={true}
            disabled={false}
            rows={2}
            value={testComments}
            onChange={handleInputChange}
          />
        </div>
        <div className="end-abtest-modal__seperator"></div>
        <div className="end-abtest-modal__actions">
          <Button
            onClick={props.onClose}
            text={'Cancel'}
            size={'medium'}
            variant={'secondary'}
            className="end-abtest-modal__actions__cancel"
          />
          <Button
            onClick={handleReviewClick}
            text={selectedActionOption === AbTestWinnerOptions.dontImplementAnyChange ? 'End Test' : 'Review'}
            disabled={isReviewDisabled}
            size={'medium'}
            variant={props.buttonVariant ?? 'primary'}
          />
        </div>
      </div>
    </Modal>
  );
};
